import React from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";

import { blogApi } from "../api/blogApi";
import { useAuth } from "../auth/authStore";
import { useNotifications } from "../notifications/notificationsStore";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import MobileQuickNav from "../components/layout/MobileQuickNav";
import { ensureAbsUrl } from "../utils/seo";

const STATUS_TABS = [
  { key: "ALL", label: "Все" },
  { key: "PUBLISHED", label: "Опубликованные" },
  { key: "DRAFT", label: "Черновики" },
  { key: "SCHEDULED", label: "Отложенные" },
  { key: "ARCHIVED", label: "Архив" },
];

const SORT_OPTIONS = [
  { value: "UPDATED_DESC", label: "По дате изменения" },
  { value: "TITLE_ASC", label: "По названию" },
  { value: "POPULARITY_DESC", label: "По популярности" },
  { value: "STATUS", label: "По статусу" },
];

const STATUS_PRIORITY = {
  PUBLISHED: 1,
  SCHEDULED: 2,
  DRAFT: 3,
  ARCHIVED: 4,
};

const CATEGORY_VISUALS = {
  Тревожность: { accent: "lav", emoji: "🧠", coverAccent: "Тревожность" },
  Выгорание: { accent: "amber", emoji: "🔥", coverAccent: "Выгорание" },
  Отношения: { accent: "pink", emoji: "💬", coverAccent: "Отношения" },
  Самооценка: { accent: "lav", emoji: "🧩", coverAccent: "Самооценка" },
  Травма: { accent: "mint", emoji: "🌱", coverAccent: "Травма" },
  Родительство: { accent: "amber", emoji: "🌙", coverAccent: "Родительство" },
  Практика: { accent: "mint", emoji: "🌿", coverAccent: "Практика" },
  Осознанность: { accent: "lav", emoji: "✨", coverAccent: "Осознанность" },
  Привычки: { accent: "teal", emoji: "🪴", coverAccent: "Привычки" },
};

const FALLBACK_VISUALS = [
  { accent: "mint", emoji: "✨", coverAccent: "Статья" },
  { accent: "lav", emoji: "🧠", coverAccent: "Блог" },
  { accent: "amber", emoji: "🌿", coverAccent: "Практика" },
  { accent: "pink", emoji: "💡", coverAccent: "Разбор" },
  { accent: "teal", emoji: "🪴", coverAccent: "Польза" },
];

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function normalizeStatus(post = {}) {
  const raw = String(post?.status || post?.publicationStatus || post?.state || post?.postStatus || post?.moderationStatus || "PUBLISHED")
    .trim()
    .toUpperCase();
  if (raw === "SCHEDULE" || raw === "PLANNED") return "SCHEDULED";
  if (raw === "PUBLISH" || raw === "PUBLIC") return "PUBLISHED";
  if (raw === "ARCHIVE") return "ARCHIVED";
  if (["DRAFT", "SCHEDULED", "PUBLISHED", "ARCHIVED"].includes(raw)) return raw;
  return raw || "PUBLISHED";
}

function statusLabel(status) {
  if (status === "PUBLISHED") return "Опубликовано";
  if (status === "SCHEDULED") return "Отложено";
  if (status === "DRAFT") return "Черновик";
  if (status === "ARCHIVED") return "Архив";
  return "Неизвестно";
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Moscow",
  })
    .format(date)
    .replace(" г.", "");
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Europe/Moscow",
  })
    .format(date)
    .replace(" г.", "");
}

function formatNumber(value) {
  return new Intl.NumberFormat("ru-RU").format(Math.max(0, Number(value) || 0));
}

function formatCompactNumber(value) {
  const num = Math.max(0, Number(value) || 0);
  if (num >= 1000000) return `${(num / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return formatNumber(num);
}

function stripText(html = "") {
  if (typeof document === "undefined") return String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  const div = document.createElement("div");
  div.innerHTML = String(html || "");
  return String(div.textContent || div.innerText || "").replace(/\s+/g, " ").trim();
}

function readingMinutes(post = {}) {
  const fromApi = Number(post?.readingTimeMinutes);
  if (Number.isFinite(fromApi) && fromApi > 0) return fromApi;
  const words = stripText(post?.content || post?.description || post?.excerpt || "").split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 180));
}

function postKey(post = {}) {
  return String(post?.id ?? post?.postId ?? post?.slug ?? Math.random());
}

function postEditId(post = {}) {
  return encodeURIComponent(String(post?.id ?? post?.postId ?? post?.uuid ?? post?.slug ?? ""));
}

function postPublicPath(post = {}) {
  const id = post?.id ?? post?.postId ?? null;
  const slug = String(post?.slug || "").trim();
  if (id && slug) return `/blog/${id}-${slug}`;
  if (slug) return `/blog/${slug}`;
  if (id) return `/blog/${id}`;
  return "/blog";
}

function sortByUpdatedDesc(a, b) {
  const ad = Date.parse(a?.updatedAt || a?.updatedWhen || a?.publishedAt || a?.createdAt || a?.createdWhen || 0) || 0;
  const bd = Date.parse(b?.updatedAt || b?.updatedWhen || b?.publishedAt || b?.createdAt || b?.createdWhen || 0) || 0;
  return bd - ad;
}

function getTagLabel(tag) {
  if (typeof tag === "string") return tag.trim();
  if (tag && typeof tag === "object") {
    return String(tag.label || tag.name || tag.title || tag.value || "").trim();
  }
  return "";
}

function getTags(post = {}) {
  const tags = [];
  const append = (value) => {
    const label = getTagLabel(value);
    if (label) tags.push(label);
  };

  if (Array.isArray(post?.tags)) {
    post.tags.forEach(append);
  } else if (typeof post?.tags === "string") {
    const raw = post.tags.trim();
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) parsed.forEach(append);
        else append(parsed);
      } catch {
        raw
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
          .forEach(append);
      }
    }
  } else if (post?.tags && typeof post.tags === "object") {
    if (Array.isArray(post.tags.items)) post.tags.items.forEach(append);
    else append(post.tags);
  }

  const uniqueTags = Array.from(new Set(tags.filter(Boolean)));
  if (uniqueTags.length) return uniqueTags;
  const category = String(post?.category || post?.categoryTitle || "").trim();
  return category ? [category] : [];
}

function getCategory(post = {}) {
  const tags = getTags(post);
  return tags[0] || "";
}

function getVisual(post, idx = 0) {
  const category = getCategory(post);
  if (CATEGORY_VISUALS[category]) return CATEGORY_VISUALS[category];
  return FALLBACK_VISUALS[idx % FALLBACK_VISUALS.length];
}

function decodeHtmlEntities(value = "") {
  return String(value || "")
    .replace(/&amp;/gi, "&")
    .replace(/&#38;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">");
}

function normalizeMediaCandidate(raw) {
  const value = decodeHtmlEntities(raw).trim();
  if (!value) return "";
  if (/^data:image\//i.test(value) || /^blob:/i.test(value)) return value;
  if (/^(\/|\.\/|\.\.\/|https?:\/\/)/i.test(value)) return ensureAbsUrl(value) || value;
  return "";
}

function getPreviewImage(post = {}) {
  const candidates = [
    post?.previewImageUrl,
    post?.coverImg,
    post?.coverUrl,
    post?.coverImageUrl,
    post?.imageUrl,
    post?.thumbnailUrl,
    post?.posterUrl,
    post?.seo?.ogImageUrl,
  ];
  for (const candidate of candidates) {
    const normalized = normalizeMediaCandidate(candidate);
    if (normalized) return normalized;
  }
  return "";
}

function hasAnyCover(post = {}) {
  return Boolean(getPreviewImage(post));
}

function getExcerpt(post = {}) {
  const raw = String(post?.excerpt || post?.description || stripText(post?.content) || "").trim();
  if (!raw) return "Описание пока не заполнено.";
  return raw.slice(0, 120) + (raw.length > 120 ? "…" : "");
}

function getViews(post = {}) {
  return Number(post?.viewsCount || post?.viewCount || post?.views || 0) || 0;
}

function getLikes(post = {}) {
  return Number(post?.likesCount || post?.reactionsCount || post?.favoritesCount || 0) || 0;
}

function getComments(post = {}) {
  return Number(post?.commentsCount || post?.commentCount || 0) || 0;
}

function getPopularity(post = {}) {
  return getViews(post) + getLikes(post) * 3 + getComments(post) * 2;
}

function getProgress(post = {}, status = normalizeStatus(post)) {
  if (status === "PUBLISHED" || status === "SCHEDULED") return 100;
  if (status === "ARCHIVED") return 100;

  let score = 0;
  if (String(post?.title || "").trim()) score += 22;
  if (String(post?.excerpt || post?.description || "").trim()) score += 18;
  if (stripText(post?.content || "").length > 80) score += 30;
  else if (stripText(post?.content || "").length > 20) score += 16;
  if (getPreviewImage(post)) score += 12;
  if (getTags(post).length) score += 8;
  if (readingMinutes(post) > 1) score += 10;
  return Math.max(15, Math.min(95, score));
}

function getProgressLabel(status) {
  if (status === "PUBLISHED") return "опубликовано";
  if (status === "SCHEDULED") return "запланировано";
  if (status === "ARCHIVED") return "архив";
  return "черновик";
}

function getDisplayDate(post = {}, status = normalizeStatus(post)) {
  if (status === "SCHEDULED") return post?.scheduledAt || post?.publishAt || post?.publishedAt;
  if (status === "PUBLISHED") return post?.publishedAt || post?.updatedAt || post?.createdAt || post?.createdWhen;
  return post?.updatedAt || post?.createdAt || post?.createdWhen;
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7">
      <circle cx="8.5" cy="8.5" r="5.5" />
      <path d="M13 13l4 4" strokeLinecap="round" />
    </svg>
  );
}

function GridIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3" y="3" width="5" height="5" rx="1.1" />
      <rect x="12" y="3" width="5" height="5" rx="1.1" />
      <rect x="3" y="12" width="5" height="5" rx="1.1" />
      <rect x="12" y="12" width="5" height="5" rx="1.1" />
    </svg>
  );
}

function ListIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M6 5h10M6 10h10M6 15h10" strokeLinecap="round" />
      <circle cx="3" cy="5" r="1" fill="currentColor" stroke="none" />
      <circle cx="3" cy="10" r="1" fill="currentColor" stroke="none" />
      <circle cx="3" cy="15" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

function BookmarkIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
      <path d="M6 3.5h8A1.5 1.5 0 0 1 15.5 5v11l-5.5-3-5.5 3V5A1.5 1.5 0 0 1 6 3.5Z" strokeLinejoin="round" />
    </svg>
  );
}

function DotsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
      <circle cx="5" cy="10" r="1.4" />
      <circle cx="10" cy="10" r="1.4" />
      <circle cx="15" cy="10" r="1.4" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3.5 14.8V16.5h1.7l8-8-1.7-1.7-8 8Z" />
      <path d="M10.8 6.8l1.7 1.7M12.1 5.5l1-1a1.2 1.2 0 0 1 1.7 0l.7.7a1.2 1.2 0 0 1 0 1.7l-1 1" />
    </svg>
  );
}

function DeleteIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3.8 5.5h12.4M8 5.5V4.3c0-.7.6-1.3 1.3-1.3h1.4c.7 0 1.3.6 1.3 1.3v1.2M6 5.5v10.2c0 .7.6 1.3 1.3 1.3h5.4c.7 0 1.3-.6 1.3-1.3V5.5M8.6 8v6M11.4 8v6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MetricEye() {
  return <span aria-hidden="true">👁</span>;
}

function MetricHeart() {
  return <span aria-hidden="true">♡</span>;
}

function MetricComment() {
  return <span aria-hidden="true">💬</span>;
}

export default function MyBlog() {
  const navigate = useNavigate();
  const { me, role, logout } = useAuth();
  const { unreadNotificationsCount = 0 } = useNotifications();

  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("ALL");
  const [query, setQuery] = React.useState("");
  const [deletingId, setDeletingId] = React.useState("");
  const [sortBy, setSortBy] = React.useState("UPDATED_DESC");
  const [viewMode, setViewMode] = React.useState("GRID");
  const [brokenCovers, setBrokenCovers] = React.useState({});
  const [menuOpenKey, setMenuOpenKey] = React.useState("");

  const loadMine = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const page = await blogApi.listMine({ page: 0, size: 200 });
      const content = asArray(asObject(page).content || page);
      setItems(content.sort(sortByUpdatedDesc));
    } catch (err) {
      setError(String(err?.message || "Не удалось загрузить ваши статьи"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadMine();
  }, [loadMine]);

  React.useEffect(() => {
    let cancelled = false;

    const missing = items
      .filter((post) => {
        const id = post?.id ?? post?.postId;
        return id != null && id !== "" && !hasAnyCover(post);
      })
      .slice(0, 24);

    if (!missing.length) return () => {
      cancelled = true;
    };

    (async () => {
      const fetched = await Promise.all(
        missing.map(async (post) => {
          const id = post?.id ?? post?.postId;
          try {
            const full = await blogApi.getMineById(id);
            const image = getPreviewImage(full);
            if (!image) return null;
            return { id: String(id), full };
          } catch {
            return null;
          }
        })
      );

      if (cancelled) return;

      const byId = new Map(
        fetched
          .filter(Boolean)
          .map((entry) => [entry.id, entry.full])
      );
      if (!byId.size) return;

      setItems((prev) =>
        prev.map((post) => {
          const id = String(post?.id ?? post?.postId ?? "");
          const full = byId.get(id);
          if (!full) return post;
          return { ...full, ...post, previewImageUrl: getPreviewImage(full) || getPreviewImage(post) || "" };
        })
      );
    })();

    return () => {
      cancelled = true;
    };
  }, [items]);

  const stats = React.useMemo(() => {
    const base = {
      total: items.length,
      published: 0,
      draft: 0,
      scheduled: 0,
      archived: 0,
      views: 0,
      likes: 0,
      comments: 0,
    };
    items.forEach((post) => {
      const status = normalizeStatus(post);
      if (status === "PUBLISHED") base.published += 1;
      else if (status === "SCHEDULED") base.scheduled += 1;
      else if (status === "DRAFT") base.draft += 1;
      else if (status === "ARCHIVED") base.archived += 1;
      base.views += getViews(post);
      base.likes += getLikes(post);
      base.comments += getComments(post);
    });
    return base;
  }, [items]);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((post) => statusFilter === "ALL" || normalizeStatus(post) === statusFilter)
      .filter((post) => {
        if (!q) return true;
        const haystack = [
          post?.title,
          post?.description,
          post?.excerpt,
          stripText(post?.content),
          ...getTags(post),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return haystack.includes(q);
      })
      .sort((a, b) => {
        if (sortBy === "TITLE_ASC") return String(a?.title || "").localeCompare(String(b?.title || ""), "ru");
        if (sortBy === "POPULARITY_DESC") return getPopularity(b) - getPopularity(a);
        if (sortBy === "STATUS") {
          const diff = (STATUS_PRIORITY[normalizeStatus(a)] || 99) - (STATUS_PRIORITY[normalizeStatus(b)] || 99);
          if (diff !== 0) return diff;
          return sortByUpdatedDesc(a, b);
        }
        return sortByUpdatedDesc(a, b);
      });
  }, [items, query, sortBy, statusFilter]);

  const heroPost = React.useMemo(() => {
    if (!filtered.length) return null;
    return [...filtered].sort((a, b) => getPopularity(b) - getPopularity(a))[0] || filtered[0];
  }, [filtered]);

  const deletePost = React.useCallback(async (post) => {
    const id = post?.id ?? post?.postId ?? post?.uuid ?? post?.slug;
    if (!id) return;
    if (!window.confirm("Удалить статью или черновик без возможности восстановления?")) return;
    const key = String(id);
    setDeletingId(key);
    try {
      await blogApi.deleteForRole(id);
      setItems((prev) => prev.filter((item) => String(item?.id ?? item?.postId ?? item?.uuid ?? item?.slug) !== key));
    } catch (err) {
      setError(String(err?.message || "Не удалось удалить статью"));
    } finally {
      setDeletingId("");
    }
  }, []);

  const canonical = typeof window !== "undefined" ? `${window.location.origin}/dashboard/blog` : "";
  const roleToken = String(role || "CLIENT").replace(/^ROLE_/i, "").toUpperCase();
  const canCreate = ["ADMIN", "PSYCHOLOGIST", "MODERATOR"].includes(roleToken);
  const blogQuickNavItems = React.useMemo(() => [
    { to: "/blog", label: "Все статьи", icon: "☰" },
    { to: "/dashboard/blog", label: "Мои статьи", icon: "▦" },
    canCreate ? { to: "/dashboard/blog/new", label: "Создать", icon: "+", accent: "create" } : null,
  ].filter(Boolean), [canCreate]);
  const heroVisual = getVisual(heroPost || {}, 0);
  const heroTags = heroPost ? getTags(heroPost).slice(0, 4) : [];
  const heroPostKey = heroPost ? postKey(heroPost) : "";
  const heroCoverKey = heroPostKey ? `hero:${heroPostKey}` : "";
  const heroImage = heroPost ? getPreviewImage(heroPost) : "";
  const heroHasImage = Boolean(heroImage) && !brokenCovers[heroCoverKey];

  return (
    <>
      <Helmet>
        <title>Мои статьи | Бюро Души</title>
        <meta name="robots" content="noindex, nofollow" />
        {canonical ? <link rel="canonical" href={canonical} /> : null}
      </Helmet>

      <div className="podcasts-hub b-my-blog">
        <div className="podcasts-shell">
          <AppSidebarV2
            activeKey="blog"
            mobileOpen={sidebarOpen}
            userName={me?.name || me?.login || "Гость"}
            roleLabel={role || "CLIENT"}
            notificationsBadge={unreadNotificationsCount}
            onCloseMobile={() => setSidebarOpen(false)}
            onLogout={logout}
          />

          <button
            className={`podcasts-overlay ${sidebarOpen ? "is-open" : ""}`}
            type="button"
            aria-label="Закрыть меню"
            onClick={() => setSidebarOpen(false)}
          />

          <main className="podcasts-main my-blog-main">
          <header className="podcasts-topbar my-blog-topbar">
            <div className="my-blog-topbar__left">
              <button
                className="podcasts-topbar__menu-btn"
                type="button"
                onClick={() => setSidebarOpen(true)}
                aria-label="Открыть меню"
              >
                ☰
              </button>
              <div className="my-blog-topbar__title">Мои статьи</div>
              <label className="topbar-search my-blog-search">
                <SearchIcon />
                <input
                  type="text"
                  placeholder="Найти статью, черновик, тег..."
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                />
              </label>
            </div>

            <div className="my-blog-topbar__actions">
              <div className="view-toggle" aria-label="Режим отображения">
                <button
                  type="button"
                  className={`view-btn ${viewMode === "GRID" ? "active" : ""}`}
                  onClick={() => setViewMode("GRID")}
                  aria-label="Сетка"
                >
                  <GridIcon />
                </button>
                <button
                  type="button"
                  className={`view-btn ${viewMode === "LIST" ? "active" : ""}`}
                  onClick={() => setViewMode("LIST")}
                  aria-label="Список"
                >
                  <ListIcon />
                </button>
              </div>

              <select className="sort-select my-blog-sort" value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
                {SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              {canCreate ? (
                <Link className="btn-upload my-blog-create" to="/dashboard/blog/new">
                  + Новая статья
                </Link>
              ) : null}
            </div>
          </header>

          <section className="page-content my-blog-screen" onClick={() => setMenuOpenKey("")}>
            <section className="my-blog-hero-block">
              <div className={`my-blog-hero-cover cover-${heroVisual.accent}`}>
                {heroPost && heroHasImage ? (
                  <img
                    src={heroImage}
                    alt={heroPost?.title || "Обложка статьи"}
                    loading="lazy"
                    decoding="async"
                    onError={() => {
                      if (!heroCoverKey) return;
                      setBrokenCovers((prev) => ({ ...prev, [heroCoverKey]: true }));
                    }}
                  />
                ) : (
                  <>
                    <div className="my-blog-hero-cover__accent">{heroPost?.title || "Личный блог"}</div>
                    <div className="my-blog-hero-cover__emoji">{heroVisual.emoji}</div>
                  </>
                )}
                <div className="my-blog-hero-cover__glass"><BookmarkIcon /></div>
              </div>

              <div className="my-blog-hero-content">
                <div className="my-blog-hero-kicker">Личный блог</div>
                <h1>Мои статьи</h1>
                <p>
                  Управляйте своими опубликованными статьями, черновиками
                  и запланированными материалами в личном блоге.
                </p>
                <div className="my-blog-hero-tags">
                  {heroTags.length ? heroTags.map((tag, index) => <span key={`${tag}-${index}`}>{tag}</span>) : (
                    <>
                      <span>мышление</span>
                      <span>осознанность</span>
                      <span>привычки</span>
                      <span>саморазвитие</span>
                    </>
                  )}
                </div>
                <div className="my-blog-hero-actions">
                  {canCreate ? (
                    <Link className="my-blog-hero-cta" to="/dashboard/blog/new">Создать статью</Link>
                  ) : null}
                  <button type="button" className="my-blog-hero-icon-btn" aria-label="Закреплённый материал">
                    <BookmarkIcon />
                  </button>
                </div>
              </div>

              <div className="my-blog-hero-stats">
                <div className="my-blog-hero-stat">
                  <b>{formatNumber(stats.total)}</b>
                  <span>всего статей</span>
                </div>
                <div className="my-blog-hero-stat">
                  <b>{formatNumber(stats.published)}</b>
                  <span>опубликовано</span>
                </div>
                <div className="my-blog-hero-stat">
                  <b>{formatNumber(stats.draft)}</b>
                  <span>черновиков</span>
                </div>
              </div>
            </section>

            <section className="my-blog-filters">
              <div className="my-blog-filters__label">Фильтры</div>
              <div className="my-blog-filters__chips">
                {STATUS_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={`my-blog-filter-chip ${statusFilter === tab.key ? "is-active" : ""}`}
                    onClick={() => setStatusFilter(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="my-blog-filters__count">{formatNumber(filtered.length)} материала</div>
            </section>

            <section className="my-blog-section-head">
              <h2>Последние материалы</h2>
              <div className="my-blog-section-meta">
                <span>Просмотры: {formatCompactNumber(stats.views)}</span>
                <span>Реакции: {formatCompactNumber(stats.likes)}</span>
                <span>Комментарии: {formatCompactNumber(stats.comments)}</span>
              </div>
            </section>

            {error ? <div className="my-blog-alert">{error}</div> : null}

            {loading ? (
              <div className="my-blog-empty">Загружаем ваши статьи…</div>
            ) : filtered.length ? (
              <div className={`my-blog-cards my-blog-cards--${viewMode.toLowerCase()}`}>
                {filtered.map((post, index) => {
                  const status = normalizeStatus(post);
                  const id = postEditId(post);
                  const key = postKey(post);
                  const openPath = status === "PUBLISHED" ? postPublicPath(post) : `/dashboard/blog/${id}/edit`;
                  const visual = getVisual(post, index);
                  const image = getPreviewImage(post);
                  const hasImage = Boolean(image) && !brokenCovers[key];
                  const progress = getProgress(post, status);
                  const date = getDisplayDate(post, status);
                  const deletingKey = String(post?.id ?? post?.postId ?? post?.uuid ?? post?.slug);
                  const metrics = {
                    views: getViews(post),
                    likes: getLikes(post),
                    comments: getComments(post),
                  };
                  const tags = getTags(post).slice(0, 3);

                  return (
                    <article
                      key={key}
                      className={`my-blog-entry is-${status.toLowerCase()} accent-${visual.accent} is-clickable`}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        setMenuOpenKey("");
                        navigate(openPath);
                      }}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter" && event.key !== " ") return;
                        event.preventDefault();
                        setMenuOpenKey("");
                        navigate(openPath);
                      }}
                    >
                      <div className="my-blog-entry__media">
                        {hasImage ? (
                          <img
                            src={image}
                            alt={post?.title || "Обложка статьи"}
                            loading="lazy"
                            decoding="async"
                            onError={() => setBrokenCovers((prev) => ({ ...prev, [key]: true }))}
                          />
                        ) : (
                          <>
                            <div className="my-blog-entry__emoji">{visual.emoji}</div>
                            <div className="my-blog-entry__cover-accent">{visual.coverAccent}</div>
                          </>
                        )}
                      </div>

                      <div className="my-blog-entry__body">
                        <div className="my-blog-entry__topline">
                          <div className={`my-blog-status-badge is-${status.toLowerCase()}`}>{statusLabel(status)}</div>
                          <div
                            className="my-blog-entry__menu-wrap"
                            onClick={(event) => event.stopPropagation()}
                            onKeyDown={(event) => event.stopPropagation()}
                          >
                            <button
                              type="button"
                              className="my-blog-entry__more"
                              aria-label="Ещё действия"
                              title="Ещё действия"
                              onClick={() => setMenuOpenKey((prev) => (prev === key ? "" : key))}
                            >
                              <DotsIcon />
                            </button>
                            {menuOpenKey === key ? (
                              <div className="my-blog-entry__menu" role="menu" aria-label="Действия со статьёй">
                                <button
                                  type="button"
                                  role="menuitem"
                                  onClick={() => {
                                    setMenuOpenKey("");
                                    navigate(`/dashboard/blog/${id}/edit`);
                                  }}
                                >
                                  <EditIcon />
                                  <span>Редактировать</span>
                                </button>
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="is-danger"
                                  disabled={deletingId === deletingKey}
                                  onClick={() => {
                                    setMenuOpenKey("");
                                    deletePost(post);
                                  }}
                                >
                                  <DeleteIcon />
                                  <span>{deletingId === deletingKey ? "Удаление…" : "Удалить"}</span>
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </div>

                        <h3>{String(post?.title || "Без названия").trim()}</h3>
                        <p>{getExcerpt(post)}</p>

                        <div className="my-blog-entry__meta">
                          {status === "SCHEDULED" ? (
                            <span>Публикация: {formatDateTime(date)}</span>
                          ) : (
                            <span>{formatDate(date)}</span>
                          )}
                          <span>{readingMinutes(post)} мин чтения</span>
                        </div>

                        {tags.length ? (
                          <div className="my-blog-entry__tags">
                            {tags.map((tag, idx) => (
                              <span key={`${key}-${tag}-${idx}`}>{tag}</span>
                            ))}
                          </div>
                        ) : null}

                        <div className="my-blog-entry__footer">
                          <div className="my-blog-entry__progress-wrap">
                            <progress className="my-blog-entry__progress" max={100} value={progress} />
                            <div className="my-blog-entry__progress-label">
                              <strong>{progress}%</strong>
                              <span>{getProgressLabel(status)}</span>
                            </div>
                          </div>

                          <div className="my-blog-entry__actions">
                            <div className="my-blog-entry__metrics">
                              <span><MetricEye /> {formatCompactNumber(metrics.views)}</span>
                              <span><MetricComment /> {formatCompactNumber(metrics.comments)}</span>
                              <span><MetricHeart /> {formatCompactNumber(metrics.likes)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="my-blog-empty is-large">
                <b>Материалов пока нет</b>
                <span>Создайте первую статью или измените фильтр.</span>
                {canCreate ? <Link to="/dashboard/blog/new">Создать статью</Link> : null}
              </div>
            )}
          </section>
          <MobileQuickNav items={blogQuickNavItems} />
          </main>
        </div>
      </div>
    </>
  );
}
